import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { generateWhatsAppVerificationCode, verifyWhatsAppCode } from "@/hooks/subscription/whatsappVerification";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { debugLogger } from "@/utils/debug";

const DEFAULT_RESEND_COUNTDOWN_SECONDS = 60;

interface VerifyWhatsAppButtonProps {
  phoneNumber: string;
  onVerificationSuccess?: () => void;
  providerId?: string;
  isVerified?: boolean;
}

export default function VerifyWhatsAppButton({
  phoneNumber,
  onVerificationSuccess,
  providerId,
  isVerified
}: VerifyWhatsAppButtonProps) {
  const [isVerifiedState, setIsVerifiedState] = useState<boolean>(isVerified || false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [isSendingCode, setIsSendingCode] = useState<boolean>(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState<boolean>(false);
  const [resendCountdown, setResendCountdown] = useState<number>(0);
  const [sendStatusMessage, setSendStatusMessage] = useState<string>("");

  useEffect(() => {
    if (resendCountdown <= 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      setResendCountdown((current) => current - 1);
    }, 1000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [resendCountdown]);

  const sendVerificationCode = async () => {
    try {
      setIsSendingCode(true);
      setSendStatusMessage("");

      const result = await generateWhatsAppVerificationCode(providerId || null, phoneNumber, {
        deliveryMode: "automated",
      });

      if (!result.success) {
        const retryAfter = result.retryAfter ?? null;
        if (retryAfter && retryAfter > 0) {
          setResendCountdown(retryAfter);
        }

        const message = result.message || "Failed to send verification code";
        setSendStatusMessage(message);
        toast.error(message);
        return;
      }

      const nextRetryAfter = result.retryAfter && result.retryAfter > 0
        ? result.retryAfter
        : DEFAULT_RESEND_COUNTDOWN_SECONDS;
      setResendCountdown(nextRetryAfter);

      if (result.delivery === "manual_required" && result.code) {
        const message = result.message || `Automated delivery unavailable. Use backup code: ${result.code}`;
        setSendStatusMessage(message);
        toast.success(message);
        return;
      }

      const message = result.message || "Verification code sent to your WhatsApp number";
      setSendStatusMessage(message);
      toast.success(message);
    } catch (error) {
      console.error("Error sending verification code:", error);
      const message = error instanceof Error ? error.message : "Failed to send verification code";
      setSendStatusMessage(message);
      toast.error(message);
    } finally {
      setIsSendingCode(false);
    }
  };

  const openVerificationDialog = async () => {
    setIsDialogOpen(true);
    setVerificationCode("");
    await sendVerificationCode();
  };

  const closeVerificationDialog = () => {
    setIsDialogOpen(false);
    setVerificationCode("");
    setSendStatusMessage("");
    setResendCountdown(0);
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter a valid 6-digit verification code");
      return;
    }

    try {
      setIsVerifyingCode(true);
      debugLogger.log("Verifying code:", verificationCode);

      // Enhanced logging for debugging
      debugLogger.log("Verification request parameters:", {
        providerId: providerId || null,
        code: verificationCode
      });

      const verified = await verifyWhatsAppCode(providerId || null, verificationCode);

      if (verified) {
        toast.success("WhatsApp verified successfully!");
        setIsVerifiedState(true);
        closeVerificationDialog();
        if (onVerificationSuccess) {
          onVerificationSuccess();
        }
      } else {
        toast.error("Invalid verification code. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying code:", error);
      toast.error(error instanceof Error ? error.message : "Failed to verify code");
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleCodeChange = (value: string) => {
    debugLogger.log("Code changed:", value);
    setVerificationCode(value);
  };

  if (isVerifiedState) {
    return null;
  }

  return <>
      <Button variant="outline" size="sm" onClick={() => void openVerificationDialog()} className="gap-1 whitespace-nowrap" type="button" disabled={isSendingCode}>
        {isSendingCode ? "Sending..." : "Verify"}
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>WhatsApp Verification</DialogTitle>
            <DialogDescription>
              Enter the verification code that was sent to your WhatsApp number.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="mb-4">
              <p className="text-sm font-medium mb-1">WhatsApp Number</p>
              <p className="rounded-md bg-brand-mist p-2 text-sm text-brand-slate">{phoneNumber}</p>
            </div>

            <div className="space-y-4">
              {isSendingCode && <p className="text-sm text-center text-muted-foreground">Sending a verification code to your WhatsApp number...</p>}
              {!isSendingCode && sendStatusMessage && <p className="text-sm text-center text-muted-foreground">{sendStatusMessage}</p>}
              <div className="space-y-2">
                <p className="text-sm text-center">Enter the 6-digit code sent to your WhatsApp</p>
                
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={verificationCode} onChange={handleCodeChange}>
                    <InputOTPGroup className="gap-2">
                      {Array.from({
                      length: 6
                    }).map((_, i) => <InputOTPSlot key={i} index={i} />)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <div className="flex flex-col items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => void sendVerificationCode()}
                  disabled={isSendingCode || resendCountdown > 0}
                  type="button"
                >
                  {isSendingCode ? "Sending..." : resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Resend code"}
                </Button>
                {resendCountdown > 0 && (
                  <p className="text-xs text-muted-foreground text-center">
                    WhatsApp verification requests are limited to once every 60 seconds per number.
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={closeVerificationDialog} disabled={isVerifyingCode || isSendingCode} className="sm:w-full" type="button">
              Cancel
            </Button>
            <Button onClick={handleVerifyCode} disabled={isVerifyingCode || isSendingCode || verificationCode.length !== 6} className="sm:w-full bg-[#25D366] hover:bg-[#25D366]/90 text-white" type="button">
              {isVerifyingCode ? "Verifying..." : "Verify Code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
}
